import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import SettingsCard from '../structure/SettingsCard';
import ApplicationContainer from '../../state/containers/ApplicationContainer';
import OrganizationContainer from '../../state/containers/OrganizationContainer';
import ApplicationModel from '../../models/applications/api/ApplicationModel';
import { getAppliedApplicationTemplatesForApp } from '../../api/controllers/appliedApplicationTemplates';
import {
    ClientContact,
    ClientNotesData,
    createEmptyContact,
    getClientNotesMeta,
    getReturnToStoreNumberFromFields,
    parseClientNotes,
    serializeClientNotes
} from './clientNotesData';
import {
    color_colors_ocean,
    color_shades_dark,
    color_shades_darker,
    color_shades_darkest,
    color_shades_light,
    color_shades_lightest,
    color_variants_ocean_light_1,
    color_variants_shadow_10
} from '../../constants/colors';
import { breakpoint_small } from '../../constants/breakpoints';

interface SparkOrganizationClientNotesCardProps {
    orgContainer: OrganizationContainer;
}

export interface SparkApplicationClientNotesCardProps {
    appContainer: ApplicationContainer;
    commonApp?: ApplicationModel;
    locationApps?: ApplicationModel[];
}

const getContext = (value?: string) => value || '';

export const SparkOrganizationClientNotesCard: React.FC<SparkOrganizationClientNotesCardProps> = (props) => {
    const organization = props.orgContainer.state.currentOrganization;
    const [draft, setDraft] = useState<ClientNotesData>(parseClientNotes(organization?.clientContext));
    const [saved, setSaved] = useState<string>(getContext(organization?.clientContext));
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    useEffect(() => {
        const nextContext = getContext(organization?.clientContext);
        setDraft(parseClientNotes(nextContext));
        setSaved(nextContext);
        setMessage('');
    }, [organization?.id, organization?.clientContext]);

    const serializedDraft = useMemo(() => serializeClientNotes(draft), [draft]);
    const hasChanges = serializedDraft !== saved;

    const handleSave = async () => {
        if (!organization?.id || !hasChanges || isSaving) {
            return;
        }

        setIsSaving(true);
        setMessage('');
        const result = await props.orgContainer.updateOrganizationClientContext(serializedDraft);
        setIsSaving(false);

        if (result?.resultType === 'Ok') {
            setSaved(serializedDraft);
            setMessage('Organization notes saved');
            return;
        }

        setMessage(result?.errors?.[0] || 'Could not save organization notes');
    };

    return (
        <SettingsCard title="Client Notes" subtitle="Internal account notes for support, rollout, and escalation context.">
            <NotesLayout>
                <FieldHeader>
                    <FieldLabel>Organization notes</FieldLabel>
                    {message && <SaveStatus>{message}</SaveStatus>}
                </FieldHeader>
                <ContextName>{organization?.name || 'Current organization'}</ContextName>
                <NotesMeta>{getClientNotesMeta(saved)}</NotesMeta>
                <ClientNotesStructuredFields
                    idPrefix="spark-org-client-notes"
                    value={draft}
                    onChange={setDraft}
                />
                <ActionsRow>
                    <SaveButton type="button" disabled={!hasChanges || isSaving} onClick={handleSave}>
                        {isSaving ? 'Saving...' : 'Save organization notes'}
                    </SaveButton>
                </ActionsRow>
            </NotesLayout>
        </SettingsCard>
    );
};

export const SparkApplicationClientNotesEditor: React.FC<SparkApplicationClientNotesCardProps> = (props) => {
    const applications = useMemo(() => {
        const apps = [
            ...(props.commonApp ? [props.commonApp] : []),
            ...(props.locationApps || [])
        ];
        const seenIds = new Set<string>();
        return apps.filter(app => {
            if (!app?.id || seenIds.has(app.id)) {
                return false;
            }
            seenIds.add(app.id);
            return true;
        });
    }, [props.commonApp, props.locationApps]);

    const [selectedApplicationId, setSelectedApplicationId] = useState<string>('');
    const [draft, setDraft] = useState<ClientNotesData>(parseClientNotes(''));
    const [saved, setSaved] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [savedOverridesById, setSavedOverridesById] = useState<{ [applicationId: string]: string }>({});
    const [returnToStoreNumbersById, setReturnToStoreNumbersById] = useState<{ [applicationId: string]: string }>({});

    useEffect(() => {
        if (!applications.length) {
            setSelectedApplicationId('');
            return;
        }

        if (!selectedApplicationId || !applications.some(app => app.id === selectedApplicationId)) {
            setSelectedApplicationId(applications[0].id);
        }
    }, [applications, selectedApplicationId]);

    const selectedApplication = applications.find(app => app.id === selectedApplicationId);
    const selectedContext = selectedApplication
        ? savedOverridesById[selectedApplication.id] ?? getContext(selectedApplication.clientContext)
        : '';

    useEffect(() => {
        setDraft(parseClientNotes(selectedContext));
        setSaved(selectedContext);
        setMessage('');
    }, [selectedApplicationId, selectedContext]);

    useEffect(() => {
        let isMounted = true;

        const loadReturnToStoreNumber = async () => {
            if (!selectedApplicationId) {
                return;
            }

            const result = await getAppliedApplicationTemplatesForApp(selectedApplicationId);
            if (!isMounted || result?.resultType !== 'Ok') {
                return;
            }

            const fields = (result.data || []).reduce((allFields, appliedTemplate) => (
                allFields.concat(appliedTemplate.templateFormFieldValues || [])
            ), [] as any[]);
            const returnToStoreNumber = getReturnToStoreNumberFromFields(fields);

            setReturnToStoreNumbersById(currentValues => ({
                ...currentValues,
                [selectedApplicationId]: returnToStoreNumber
            }));
        };

        loadReturnToStoreNumber();

        return () => {
            isMounted = false;
        };
    }, [selectedApplicationId]);

    const serializedDraft = useMemo(() => serializeClientNotes(draft), [draft]);
    const selectedReturnToStoreNumber = selectedApplication
        ? returnToStoreNumbersById[selectedApplication.id] || ''
        : '';
    const hasChanges = serializedDraft !== saved;

    if (!applications.length) {
        return null;
    }

    const handleSave = async () => {
        if (!selectedApplication?.id || !hasChanges || isSaving) {
            return;
        }

        setIsSaving(true);
        setMessage('');
        const result = await props.appContainer.updateApplicationClientContext(selectedApplication.id, serializedDraft);
        setIsSaving(false);

        if (result?.resultType === 'Ok') {
            setSaved(serializedDraft);
            setSavedOverridesById({
                ...savedOverridesById,
                [selectedApplication.id]: serializedDraft
            });
            setMessage('Store notes saved');
            return;
        }

        setMessage(result?.errors?.[0] || 'Could not save store notes');
    };

    return (
        <NotesLayout>
            <FieldHeader>
                <FieldLabel>Store notes</FieldLabel>
                {message && <SaveStatus>{message}</SaveStatus>}
            </FieldHeader>
            <ApplicationSelect
                value={selectedApplicationId}
                onChange={(event) => setSelectedApplicationId(event.target.value)}
            >
                {applications.map(app => (
                    <option key={app.id} value={app.id}>
                        {app.name || 'Untitled assistant'}
                    </option>
                ))}
            </ApplicationSelect>
            <NotesMeta>{getClientNotesMeta(saved)}</NotesMeta>
            <ClientNotesStructuredFields
                idPrefix={`spark-app-client-notes-${selectedApplicationId}`}
                value={draft}
                returnToStoreNumber={selectedReturnToStoreNumber}
                onChange={setDraft}
            />
            <ActionsRow>
                <SaveButton type="button" disabled={!hasChanges || isSaving} onClick={handleSave}>
                    {isSaving ? 'Saving...' : 'Save store notes'}
                </SaveButton>
            </ActionsRow>
        </NotesLayout>
    );
};

interface ClientNotesStructuredFieldsProps {
    idPrefix: string;
    value: ClientNotesData;
    returnToStoreNumber?: string;
    onChange: (value: ClientNotesData) => void;
}

const ClientNotesStructuredFields: React.FC<ClientNotesStructuredFieldsProps> = ({
    idPrefix,
    value,
    returnToStoreNumber,
    onChange
}) => {
    const updateValue = (updates: Partial<ClientNotesData>) => {
        onChange({
            ...value,
            ...updates
        });
    };

    const updateContact = (contactId: string, updates: Partial<ClientContact>) => {
        updateValue({
            contacts: value.contacts.map(contact => (
                contact.id === contactId ? { ...contact, ...updates } : contact
            ))
        });
    };

    const addContact = () => {
        updateValue({
            contacts: value.contacts.concat(createEmptyContact())
        });
    };

    const removeContact = (contactId: string) => {
        updateValue({
            contacts: value.contacts.filter(contact => contact.id !== contactId)
        });
    };

    return (
        <StructuredNotesLayout>
            {returnToStoreNumber && (
                <ReadOnlyInfo>
                    <ReadOnlyInfoLabel>Transfers back to</ReadOnlyInfoLabel>
                    <ReadOnlyInfoValue>{returnToStoreNumber}</ReadOnlyInfoValue>
                </ReadOnlyInfo>
            )}

            <FieldGroup>
                <InlineFieldLabel htmlFor={`${idPrefix}-public-phone`}>Public phone number</InlineFieldLabel>
                <TextInput
                    id={`${idPrefix}-public-phone`}
                    value={value.publicPhoneNumber}
                    onChange={(event) => updateValue({ publicPhoneNumber: event.target.value })}
                    placeholder="Number customers call before calls forward to Voicify"
                />
            </FieldGroup>

            <ContactsHeader>
                <SectionTitle>Contacts</SectionTitle>
                <AddContactButton type="button" onClick={addContact}>
                    Add contact
                </AddContactButton>
            </ContactsHeader>

            {value.contacts.length === 0 && (
                <EmptyContacts>No contacts added yet.</EmptyContacts>
            )}

            {value.contacts.map((contact, index) => (
                <ContactPanel key={contact.id}>
                    <ContactPanelHeader>
                        <ContactTitle>Contact {index + 1}</ContactTitle>
                        <RemoveContactButton type="button" onClick={() => removeContact(contact.id)}>
                            Remove
                        </RemoveContactButton>
                    </ContactPanelHeader>

                    <ContactGrid>
                        <FieldGroup>
                            <InlineFieldLabel htmlFor={`${idPrefix}-name-${contact.id}`}>Name</InlineFieldLabel>
                            <TextInput
                                id={`${idPrefix}-name-${contact.id}`}
                                value={contact.name}
                                onChange={(event) => updateContact(contact.id, { name: event.target.value })}
                                placeholder="Contact name"
                            />
                        </FieldGroup>

                        <FieldGroup>
                            <InlineFieldLabel htmlFor={`${idPrefix}-phone-${contact.id}`}>Phone number</InlineFieldLabel>
                            <TextInput
                                id={`${idPrefix}-phone-${contact.id}`}
                                value={contact.phoneNumber}
                                onChange={(event) => updateContact(contact.id, { phoneNumber: event.target.value })}
                                placeholder="Direct phone number"
                            />
                        </FieldGroup>

                        <FieldGroup>
                            <InlineFieldLabel htmlFor={`${idPrefix}-email-${contact.id}`}>Email</InlineFieldLabel>
                            <TextInput
                                id={`${idPrefix}-email-${contact.id}`}
                                value={contact.email}
                                onChange={(event) => updateContact(contact.id, { email: event.target.value })}
                                placeholder="name@example.com"
                            />
                        </FieldGroup>

                        <FieldGroup>
                            <InlineFieldLabel htmlFor={`${idPrefix}-description-${contact.id}`}>Description</InlineFieldLabel>
                            <SmallTextarea
                                id={`${idPrefix}-description-${contact.id}`}
                                value={contact.description}
                                onChange={(event) => updateContact(contact.id, { description: event.target.value })}
                                placeholder="Role, escalation details, best time to reach..."
                            />
                        </FieldGroup>
                    </ContactGrid>
                </ContactPanel>
            ))}

            <FieldGroup>
                <InlineFieldLabel htmlFor={`${idPrefix}-additional`}>Additional notes</InlineFieldLabel>
                <NotesTextarea
                    id={`${idPrefix}-additional`}
                    value={value.notes}
                    onChange={(event) => updateValue({ notes: event.target.value })}
                    placeholder="Rollout details, local reminders, escalation notes..."
                    rows={5}
                />
            </FieldGroup>
        </StructuredNotesLayout>
    );
};

export const SparkApplicationClientNotesCard: React.FC<SparkApplicationClientNotesCardProps> = (props) => {
    return (
        <SettingsCard title="Assistant Notes" subtitle="Internal store notes that help support find and manage the right assistant.">
            <SparkApplicationClientNotesEditor {...props} />
        </SettingsCard>
    );
};

const NotesLayout = styled.div`
    display: grid;
    gap: 14px;
`;

const FieldHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    ${breakpoint_small} {
        align-items: flex-start;
        flex-direction: column;
        gap: 6px;
    }
`;

const FieldLabel = styled.label`
    color: ${color_shades_darkest};
    font-family: Muli, sans-serif;
    font-size: 16px;
    font-weight: 700;
`;

const SaveStatus = styled.div`
    color: ${color_shades_darker};
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 600;
`;

const ContextName = styled.div`
    color: ${color_shades_darker};
    font-family: Muli, sans-serif;
    font-size: 13px;
    font-weight: 600;
`;

const NotesMeta = styled.div`
    justify-self: start;
    border-radius: 999px;
    background: ${color_shades_lightest};
    color: ${color_shades_darker};
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 700;
    padding: 6px 10px;
`;

const fieldStyles = `
    width: 100%;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    background: ${color_shades_lightest};
    color: ${color_shades_darkest};
    font-family: Muli, sans-serif;
    font-size: 14px;
    line-height: 1.45;
    outline: none;
    transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;

    &:focus {
        border-color: ${color_colors_ocean};
        background: #ffffff;
        box-shadow: 0 0 0 4px ${color_variants_ocean_light_1};
    }
`;

const ApplicationSelect = styled.select`
    ${fieldStyles}
    min-height: 44px;
    padding: 0 12px;
`;

const StructuredNotesLayout = styled.div`
    display: grid;
    gap: 14px;
`;

const FieldGroup = styled.div`
    display: grid;
    gap: 7px;
`;

const InlineFieldLabel = styled.label`
    color: ${color_shades_darker};
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 800;
`;

const ReadOnlyInfo = styled.div`
    display: grid;
    gap: 6px;
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 8px 18px ${color_variants_shadow_10};
    padding: 12px;
`;

const ReadOnlyInfoLabel = styled.div`
    color: ${color_shades_darker};
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 800;
`;

const ReadOnlyInfoValue = styled.div`
    color: ${color_shades_darkest};
    font-family: Muli, sans-serif;
    font-size: 15px;
    font-weight: 800;
    line-height: 1.25;
    overflow-wrap: anywhere;
`;

const TextInput = styled.input`
    ${fieldStyles}
    min-height: 44px;
    padding: 0 12px;
`;

const ContactsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const SectionTitle = styled.div`
    color: ${color_shades_darkest};
    font-family: Muli, sans-serif;
    font-size: 15px;
    font-weight: 800;
`;

const AddContactButton = styled.button`
    flex: 0 0 auto;
    min-height: 34px;
    border: 1px solid ${color_shades_dark};
    border-radius: 999px;
    background: ${color_shades_lightest};
    color: ${color_shades_darkest};
    cursor: pointer;
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 800;
    padding: 0 12px;
`;

const EmptyContacts = styled.div`
    min-height: 52px;
    display: flex;
    align-items: center;
    border: 1px dashed ${color_shades_dark};
    border-radius: 8px;
    background: ${color_shades_lightest};
    color: ${color_shades_darker};
    font-family: Muli, sans-serif;
    font-size: 13px;
    font-weight: 700;
    padding: 0 12px;
`;

const ContactPanel = styled.div`
    display: grid;
    gap: 12px;
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 8px 18px ${color_variants_shadow_10};
    padding: 12px;
`;

const ContactPanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const ContactTitle = styled.div`
    color: ${color_shades_darkest};
    font-family: Muli, sans-serif;
    font-size: 13px;
    font-weight: 800;
`;

const RemoveContactButton = styled.button`
    border: none;
    background: none;
    color: #9d3f2f;
    cursor: pointer;
    font-family: Muli, sans-serif;
    font-size: 12px;
    font-weight: 800;
    padding: 0;
`;

const ContactGrid = styled.div`
    display: grid;
    gap: 12px;

    @media (min-width: 720px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const SmallTextarea = styled.textarea`
    ${fieldStyles}
    min-height: 86px;
    padding: 12px;
    resize: vertical;
`;

const NotesTextarea = styled.textarea`
    ${fieldStyles}
    min-height: 128px;
    padding: 12px;
    resize: vertical;
`;

const ActionsRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const SaveButton = styled.button`
    min-height: 44px;
    min-width: 190px;
    border: 0;
    border-radius: 999px;
    background: ${color_colors_ocean};
    box-shadow: 0 10px 24px ${color_variants_shadow_10};
    color: #ffffff;
    cursor: pointer;
    font-family: Muli, sans-serif;
    font-size: 14px;
    font-weight: 800;
    padding: 0 24px;

    &:disabled {
        background: ${color_shades_light};
        box-shadow: none;
        color: ${color_shades_darker};
        cursor: not-allowed;
    }

    ${breakpoint_small} {
        width: 100%;
    }
`;
